/*****************************************************************************/
/* @author ml <youngmysteriouslight@gmail.com>                               */
/* @date Sat, 07 Oct 2017                                                    */
/*****************************************************************************/

var _ = require('lodash');

// dependencies, by default taken from Lodash library
var
  isObject  = _.isObject,
  mapValues = _.mapValues,
  keys      = _.keys,
  isEqual   = _.isEqual,
  assign    = _.assign,
  clone     = _.clone,
  every     = _.every;

function identity(obj) { return obj; }

/**
 * The equality is understood as an equality of sets of keys and
 *   a reference identity of corresponding values.
 * If any of two arguments is not an object, they are compared with ===.
 * @param x - JS value, mandatory
 * @param y - JS value, mandatory
 * @param skipKeysCheck - whether to check an equality of key sets, optional
 * @return true or false
 */
function eq(x, y, skipKeysCheck) {
  return x === y || (
    isObject(x) &&
    isObject(y) &&
    (skipKeysCheck || isEqual(keys(x).sort(), keys(y).sort)) &&
    every(x, function(v, key) { return y[key] === v; })
  );
}

/**
 * smartDeepMap creates a map-like function that traverses an object in deep.
 *
 * // ARGUMENTS
 * A mandatory argument `ker` specifies a function to map.
 * An optional argument `pred` is a predicate specifying whether smartDeepMap
 *   should go deep in object.
 * If `pred` is undefined, a predicate is defined by undefined result of `ker`.
 *
 * // RESULT
 * `smaptDeepMap(ker, pred)` returns a unary function `map` of object
 *   that returns an object with the similar structure up to what `ker` changed.
 *
 * // ALGORITHM (SEMI-FORMAL)
 * The basic algorithm of `map(obj)` is:
 *   1. apply `ker` to an object `obj`, take `v`.
 *   2. If `pred` is defined, apply `pred` to `obj`, take `p`;
 *      otherwise set `p` to `v === undefined`.
 *   3. If `p`, apply `map` recursively to all direct subobject (i.e. fields)
 *      of `v` and assemble a new object `u`
 *   4. If (1) `v` and `obj` are equal and
 *         (2) all the subobjects of `u` and `obj` are equal,
 *      return `obj`, otherwise return `u`.
 *
 * // ALGORITHM (INFORMAL)
 * The first part is just a single step of iterative `ker` application.
 * The second part coordinates control-flow of recursive calls
 *   and terminates recursion when needed.
 * The third part is an ordinary recursion.
 * The last part is an important one: it states that initial (sub)object
 *   should be returned (used) if no significant change was done.
 * The last condition is represented with two semi-formal conditions:
 * `v ~ obj` and `u[key] ~ obj[key]` for each `key`.
 * The equality is understood as an equality of sets of keys and
 *   a reference identity of corresponding values (see `eq` local function)
 * Note that it does not imply reference identity of comparing objects.
 *
 * Note: unlike fold/reduce which applies folding function to the result of
 *   recursive folding, smartDeepMap apply `ker` to an entire object first
 *   and than goes down to values of the result of `ker`.
 *      
 * @param ker - a kernal unary function applying to subobject, mandatory
 * @param pred - a binary predicate, optional,
 *   1st argument is a result of `ker`,
 *   2nd argument is an initial object that `ker` was applied to.
 */
function smartDeepMap(ker, pred) {
  if(typeof ker != "function") {
    console.error("ker is not a function. Returning identity function");
    return identity;
  }
  if(pred !== undefined && typeof pred != "function") {
    console.error("pred is not a function although defined");
    pred = undefined;
  }
  return function map(obj) {
    function equalValues(subobj, key) {
      return eq(subobj, obj[key]);
    }
    var v = ker(obj);
    if(v === undefined && !pred) return obj;
    if(!isObject(v)) return v;
    if(pred ? pred(v, obj) : v !== undefined) {
      var u = assign(clone(v), mapValues(v, map));
      return (eq(v, obj) && every(u, equalValues)) ? obj : u;
    } else {
      return (!pred || eq(v, obj)) ? obj : v;
    }
  }
}

module.exports = smartDeepMap;
